import {
  Attribute,
  BelongsTo,
  JsonApiModel,
  JsonApiModelConfig,
} from '@elearnio/angular2-jsonapi';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Course } from '@models/course.model';
import { Meeting } from '@models/meeting.model';
import { TrainingSchedule } from '@models/training-schedule/training-schedule.model';
import { TrainingScheduleIltCourse } from '@app/models/training-schedule/training-schedule-ilt-course.model';
import { ITEM_TYPES } from '@app/core/constants/training-schedules.constant';
import { ILTCourse } from '../ilt-course.model';
import { Member } from '../member.model';
import { PhysicalLocation } from '../physical-location.model';
import { VirtualLocation } from '../virtual-location.model';
import { getTrainingScheduleTimeString } from '@app/core/helper/training-schedule';

@JsonApiModelConfig({
  type: 'schedule_items',
})
export class TrainingScheduleItem extends JsonApiModel {
  @Attribute({ serializedName: 'created_at' })
  createdAt!: Date;

  @Attribute({ serializedName: 'updated_at' })
  updatedAt!: Date;

  @Attribute({ serializedName: 'item_id' })
  itemId!: string;

  @Attribute({ serializedName: 'item_type' })
  itemType!: string;

  @Attribute({ serializedName: 'day_number' })
  day!: number;

  @Attribute({ serializedName: 'start_time' })
  startTime!: string | null;

  @BelongsTo({ key: 'schedule' })
  trainingSchedule!: TrainingSchedule;

  @BelongsTo({ key: 'item' })
  item!: Course | Meeting | TrainingScheduleIltCourse;

  @BelongsTo({ key: 'meeting_resource' })
  meeting!: Meeting | undefined;

  @BelongsTo({ key: 'scheduled_ilt_course_resource' })
  scheduledIltCourse!: TrainingScheduleIltCourse | undefined;

  public get iltCourse(): ILTCourse | null | undefined {
    if (this.itemType === ITEM_TYPES.SCHEDULED_ILT_COURSE) {
      return this.scheduledIltCourse?.iltCourse;
    }

    return undefined;
  }

  public get instructors(): Member[] | undefined {
    if (this.itemType === ITEM_TYPES.SCHEDULED_ILT_COURSE) {
      return this.scheduledIltCourse?.instructors;
    }

    return undefined;
  }

  public get host(): Member | null | undefined {
    if (this.itemType === ITEM_TYPES.SCHEDULED_ILT_COURSE) {
      return this.scheduledIltCourse?.host;
    }

    return undefined;
  }

  public get physicalLocation(): PhysicalLocation | null | undefined {
    if (this.itemType === ITEM_TYPES.SCHEDULED_ILT_COURSE) {
      return this.scheduledIltCourse?.physicalLocation;
    }

    return undefined;
  }

  public get virtualLocation(): VirtualLocation | null | undefined {
    if (this.itemType === ITEM_TYPES.SCHEDULED_ILT_COURSE) {
      return this.scheduledIltCourse?.virtualLocation;
    }

    return undefined;
  }

  public get startTimeFormatted(): string | null {
    if (!this.startTime) {
      return null;
    }

    return getTrainingScheduleTimeString(this.startTime);
  }

  public get endTimeFormatted(): string {
    let itemTime = 15;
    const minutes =
      this.itemType === ITEM_TYPES.COURSE
        ? this.item.estimatedTimeInMinutes
        : this.item.durationInMinutes;
    if (minutes > 15) {
      itemTime = Math.ceil(minutes / 15) * 15;
    }

    dayjs.extend(customParseFormat);
    return dayjs(this.startTimeFormatted, 'HH:mm')
      .add(itemTime, 'minutes')
      .format('HH:mm');
  }
}
